/**
 * Binance REST API Functions
 *
 * REST endpoint helpers for Binance spot market data.
 * @see https://developers.binance.com/docs/binance-spot-api-docs/rest-api
 */

import type {
  BinanceDepthRequest,
  BinanceDepthResponse,
  BinanceExchangeInfoResponse,
  BinanceKlinesRequest,
  BinanceKlinesResponse,
  BinanceTicker24hRequest,
  BinanceTicker24hResponse,
  BinanceTradesRequest,
  BinanceTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.binance.com';

export const binanceRestEndpoints = {
  exchangeInfo: `${BASE_URL}/api/v3/exchangeInfo`,
  depth: `${BASE_URL}/api/v3/depth`,
  klines: `${BASE_URL}/api/v3/klines`,
  trades: `${BASE_URL}/api/v3/trades`,
  ticker24h: `${BASE_URL}/api/v3/ticker/24hr`,
} as const;

/**
 * Fetches exchange info (tick size, precision, filters) for the provided symbol.
 */
export const getBinanceExchangeInfo = async (
  symbol: string,
  options?: RestRequestOptions,
): Promise<BinanceExchangeInfoResponse> => {
  const response = await get<BinanceExchangeInfoResponse>({
    url: binanceRestEndpoints.exchangeInfo,
    params: { symbol },
    options,
  });

  return response.data;
};

/**
 * Retrieves the orderbook snapshot for the symbol.
 * @see https://developers.binance.com/docs/binance-spot-api-docs/rest-api/market-data-endpoints#order-book
 */
export const getBinanceDepth = async (
  params: BinanceDepthRequest,
  options?: RestRequestOptions,
): Promise<BinanceDepthResponse> => {
  const response = await get<BinanceDepthResponse, BinanceDepthRequest>({
    url: binanceRestEndpoints.depth,
    params,
    options,
  });

  return response.data;
};

/**
 * Requests historical klines (candlestick data) for the symbol and interval.
 *
 * Klines are uniquely identified by their open time.
 *
 * Notes:
 * - If startTime and endTime are not sent, the most recent klines are returned
 * - If timeZone is provided, kline intervals are interpreted in that timezone instead of UTC
 * - startTime and endTime are always interpreted in UTC, regardless of timeZone
 * - Default limit: 500; Maximum: 1000
 * - Weight: 2
 *
 * @param params - Request parameters including symbol, interval, and optional filters
 * @param options - Optional request configuration (timeout, abort signal, etc.)
 * @returns Array of kline tuples with OHLCV data and additional fields
 * @see https://developers.binance.com/docs/binance-spot-api-docs/rest-api/market-data-endpoints#klinecandlestick-data
 */
export const getBinanceKlines = async (
  params: BinanceKlinesRequest,
  options?: RestRequestOptions,
): Promise<BinanceKlinesResponse> => {
  const response = await get<BinanceKlinesResponse, BinanceKlinesRequest>({
    url: binanceRestEndpoints.klines,
    params,
    options,
  });

  return response.data;
};

/**
 * Returns the most recent public trades.
 * @see https://developers.binance.com/docs/binance-spot-api-docs/rest-api/market-data-endpoints#recent-trades-list
 */
export const getBinanceTrades = async (
  params: BinanceTradesRequest,
  options?: RestRequestOptions,
): Promise<BinanceTradesResponse> => {
  const response = await get<BinanceTradesResponse, BinanceTradesRequest>({
    url: binanceRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches 24h ticker statistics.
 * @see https://developers.binance.com/docs/binance-spot-api-docs/rest-api/market-data-endpoints#24hr-ticker-price-change-statistics
 */
export const getBinanceTicker24h = async (
  params: BinanceTicker24hRequest,
  options?: RestRequestOptions,
): Promise<BinanceTicker24hResponse> => {
  const response = await get<BinanceTicker24hResponse, BinanceTicker24hRequest>({
    url: binanceRestEndpoints.ticker24h,
    params,
    options,
  });

  return response.data;
};
